/**
 * The sauna chimney, drawn as bought: one owner for every engine (square,
 * two-room via buildSaunaRoomCore, barrel).
 *
 *   - a bare Ø115 smoke pipe from the stove collar up to the insulated
 *     chimney, which comes down at least 430 mm below the ceiling
 *     (domain/chimney/chimneyStack — Harvia WHP1500 manual);
 *   - the Ø220 insulated chimney as the quote's sections, base first, each
 *     joint drawn as a band, ending on the outlet height the rules ask for;
 *   - the rain cap on top, a premium slot (scripts/blender/chimney-parts.py)
 *     with a procedural fallback. It is swept from the top through the cap,
 *     so there is no sweep door.
 *
 * Roof/ceiling penetration plates stay with each engine (their surfaces
 * differ) but size themselves from `CHIMNEY_OUTER_RADIUS_M`.
 */
import * as THREE from "three";
import {
	CHIMNEY_OUTER_DIAMETER_MM,
	CHIMNEY_SMOKE_PIPE_DIAMETER_MM,
	type ChimneyStack,
} from "../domain/chimney/chimneyStack";
import { matChimney, matRoofFlashing } from "./materials";
import { CHIMNEY_CAP_ASSET_PATH, markPremiumSlot } from "./premiumDetails";

export const CHIMNEY_OUTER_RADIUS_M = CHIMNEY_OUTER_DIAMETER_MM / 2000;
export const CHIMNEY_SMOKE_PIPE_RADIUS_M =
	CHIMNEY_SMOKE_PIPE_DIAMETER_MM / 2000;
/**
 * The rain cap GLB was modelled on a Ø280 pipe end; the slot scales it in
 * plan to the Ø220 chimney so its sleeve still sits on the mantle.
 */
const RAIN_CAP_MODEL_RADIUS_M = 0.14;
/** The insulated chimney's closed bottom end, where the smoke pipe enters. */
const CHIMNEY_BOTTOM_PLATE_H_M = 0.02;
const JOINT_H_M = 0.03;
const JOINT_PROUD_M = 0.004;
/** How far a truncated (through-house) run shows above the sauna roof, at least. */
export const THROUGH_HOUSE_STUB_M = 0.3;

export interface ChimneyStackOptions {
	group: THREE.Group;
	namePrefix: string;
	/** Pipe axis. */
	x: number;
	z: number;
	/** Finished floor the stove stands on. */
	floorY: number;
	stack: ChimneyStack;
	/**
	 * Draw whole sections only until the run stands `THROUGH_HOUSE_STUB_M`
	 * clear of this height, without the rain cap — for a run that continues
	 * through a house the model does not draw. Omitted: the full stack the
	 * quote buys.
	 */
	stopAboveY?: number;
}

export interface ChimneyStackResult {
	collarY: number;
	/** Bottom of the insulated chimney, where the smoke pipe enters it. */
	chimneyBottomY: number;
	topY: number;
}

function cylinder(
	radius: number,
	y0: number,
	y1: number,
	options: ChimneyStackOptions,
	name: string,
	material: THREE.Material,
): THREE.Mesh {
	const mesh = new THREE.Mesh(
		new THREE.CylinderGeometry(radius, radius, y1 - y0, 32),
		material,
	);
	mesh.position.set(options.x, (y0 + y1) / 2, options.z);
	mesh.castShadow = true;
	mesh.receiveShadow = true;
	mesh.name = `detail/chimney/${options.namePrefix}-${name}`;
	options.group.add(mesh);
	return mesh;
}

export function buildChimneyStack(
	options: ChimneyStackOptions,
): ChimneyStackResult {
	const { stack, floorY } = options;
	const collarY = floorY + stack.flueOutletHeightMm / 1000;
	const chimneyBottomY = floorY + stack.chimneyBottomMm / 1000;
	// Joint k sits on top of section k, counted from the base.
	const jointsY: number[] = [];
	let y = chimneyBottomY;
	for (const sectionMm of stack.sectionsMm) {
		y += sectionMm / 1000;
		jointsY.push(y);
	}
	// Whole sections until the run visibly continues past `stopAboveY`.
	let drawnSections = jointsY.length;
	if (options.stopAboveY !== undefined) {
		const stubY = options.stopAboveY + THROUGH_HOUSE_STUB_M;
		const reaching = jointsY.findIndex((jointY) => jointY >= stubY);
		if (reaching !== -1) drawnSections = reaching + 1;
	}
	const topY = jointsY[drawnSections - 1];

	cylinder(
		CHIMNEY_SMOKE_PIPE_RADIUS_M,
		collarY,
		chimneyBottomY + CHIMNEY_BOTTOM_PLATE_H_M,
		options,
		"smoke-pipe",
		matChimney,
	);
	cylinder(
		CHIMNEY_OUTER_RADIUS_M,
		chimneyBottomY,
		topY,
		options,
		"pipe",
		matChimney,
	);
	cylinder(
		CHIMNEY_OUTER_RADIUS_M + JOINT_PROUD_M,
		chimneyBottomY,
		chimneyBottomY + CHIMNEY_BOTTOM_PLATE_H_M,
		options,
		"bottom-plate",
		matRoofFlashing,
	);

	// One band per joint between bought sections.
	for (let index = 1; index < drawnSections; index += 1) {
		const jointY = jointsY[index - 1];
		cylinder(
			CHIMNEY_OUTER_RADIUS_M + JOINT_PROUD_M,
			jointY - JOINT_H_M / 2,
			jointY + JOINT_H_M / 2,
			options,
			`section-joint-${index}`,
			matRoofFlashing,
		);
	}

	if (drawnSections === jointsY.length) buildRainCap(options, topY);
	return { collarY, chimneyBottomY, topY };
}

/** Regnhuv on the top section. Origin = top centre of the pipe. */
function buildRainCap(options: ChimneyStackOptions, topY: number) {
	const slot = new THREE.Group();
	slot.name = `detail/chimney/${options.namePrefix}-rain-cap`;
	slot.position.set(options.x, topY, options.z);
	const planScale = CHIMNEY_OUTER_RADIUS_M / RAIN_CAP_MODEL_RADIUS_M;
	slot.scale.set(planScale, 1, planScale);
	options.group.add(slot);
	markPremiumSlot(slot, {
		assetPath: CHIMNEY_CAP_ASSET_PATH,
		origin: [0, 0, 0],
	});

	// Built at the model radius; the slot scales it to the pipe.
	const pipeR = RAIN_CAP_MODEL_RADIUS_M;
	const postH = 0.08;
	const capR = pipeR + 0.07;
	for (let index = 0; index < 4; index += 1) {
		const angle = (index * Math.PI * 2) / 4;
		const post = new THREE.Mesh(
			new THREE.CylinderGeometry(0.006, 0.006, postH, 6),
			matRoofFlashing,
		);
		post.position.set(
			Math.cos(angle) * (pipeR - 0.02),
			postH / 2,
			Math.sin(angle) * (pipeR - 0.02),
		);
		post.castShadow = true;
		post.name = `detail/chimney/${options.namePrefix}-cap-post-${index}`;
		slot.add(post);
	}
	const brim = new THREE.Mesh(
		new THREE.CylinderGeometry(capR, capR, 0.008, 32),
		matRoofFlashing,
	);
	brim.position.set(0, postH + 0.004, 0);
	brim.castShadow = true;
	brim.name = `detail/chimney/${options.namePrefix}-cap-brim`;
	slot.add(brim);
	const cone = new THREE.Mesh(
		new THREE.ConeGeometry(capR, 0.07, 32),
		matRoofFlashing,
	);
	cone.position.set(0, postH + 0.008 + 0.035, 0);
	cone.castShadow = true;
	cone.receiveShadow = true;
	cone.name = `detail/chimney/${options.namePrefix}-roof-cap`;
	slot.add(cone);
}
